import { useEffect } from 'react';
import { useDashboard } from './useDashboard';

interface KeyboardShortcut {
  key: string;
  ctrlKey?: boolean;
  altKey?: boolean;
  shiftKey?: boolean;
  callback: () => void;
  description?: string;
}

export const useKeyboardShortcuts = () => {
  const { toggleEditMode, setSearchQuery, clearSearch } = useDashboard();

  useEffect(() => {
    const shortcuts: KeyboardShortcut[] = [
      {
        key: 'k',
        ctrlKey: true,
        callback: () => {
          // Focus search input
          const searchInput = document.querySelector('[data-search-input]') as HTMLInputElement;
          if (searchInput) {
            searchInput.focus();
            searchInput.select();
          }
        },
        description: 'Open search (Ctrl+K)'
      },
      {
        key: 'e',
        ctrlKey: true,
        callback: () => {
          toggleEditMode();
        },
        description: 'Toggle edit mode (Ctrl+E)'
      },
      {
        key: 'Escape',
        callback: () => {
          // Clear search if active, otherwise exit edit mode
          const searchInput = document.querySelector('[data-search-input]') as HTMLInputElement;
          if (searchInput && document.activeElement === searchInput) {
            clearSearch();
            searchInput.blur();
          }
        },
        description: 'Clear search or exit (Escape)'
      },
      {
        key: 'n',
        ctrlKey: true,
        callback: () => {
          // Trigger new tile creation
          const event = new CustomEvent('dashboard:new-tile');
          document.dispatchEvent(event);
        },
        description: 'New tile (Ctrl+N)'
      },
      {
        key: 'g',
        ctrlKey: true,
        callback: () => {
          // Trigger new group creation
          const event = new CustomEvent('dashboard:new-group');
          document.dispatchEvent(event);
        },
        description: 'New group (Ctrl+G)'
      },
      {
        key: 's',
        ctrlKey: true,
        callback: () => {
          // Trigger save
          const event = new CustomEvent('dashboard:save');
          document.dispatchEvent(event);
        },
        description: 'Save dashboard (Ctrl+S)'
      }
    ];

    const handleKeyDown = (event: KeyboardEvent) => {
      // Don't trigger shortcuts when typing in inputs (except for specific cases)
      const activeElement = document.activeElement;
      const isInputActive = activeElement?.tagName === 'INPUT' || 
                           activeElement?.tagName === 'TEXTAREA' || 
                           activeElement?.hasAttribute('contenteditable');

      // Allow Ctrl+K even when input is active for search
      const isSearchShortcut = event.ctrlKey && event.key.toLowerCase() === 'k';
      
      // Allow Escape to work in inputs
      const isEscapeKey = event.key === 'Escape';

      if (isInputActive && !isSearchShortcut && !isEscapeKey) {
        return;
      }

      for (const shortcut of shortcuts) {
        const keyMatches = event.key.toLowerCase() === shortcut.key.toLowerCase();
        const ctrlMatches = !!shortcut.ctrlKey === event.ctrlKey;
        const altMatches = !!shortcut.altKey === event.altKey;
        const shiftMatches = !!shortcut.shiftKey === event.shiftKey;

        if (keyMatches && ctrlMatches && altMatches && shiftMatches) {
          event.preventDefault();
          event.stopPropagation();
          shortcut.callback();
          break;
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [toggleEditMode, setSearchQuery, clearSearch]);

  // Return shortcuts for documentation/help purposes
  const getShortcuts = () => [
    'Ctrl+K - Open search',
    'Ctrl+E - Toggle edit mode',
    'Ctrl+N - New tile',
    'Ctrl+G - New group',
    'Ctrl+S - Save dashboard',
    'Escape - Clear search or exit'
  ];

  return { getShortcuts };
};